import { CompletionContext, CompletionResult } from '@codemirror/autocomplete';

// Common Python imports for data science
const COMMON_IMPORTS = {
  'numpy': ['array', 'zeros', 'ones', 'arange', 'linspace', 'random', 'mean', 'std', 'sum'],
  'pandas': ['DataFrame', 'Series', 'read_csv', 'read_excel', 'concat', 'merge', 'pivot_table'],
  'matplotlib.pyplot': ['plot', 'scatter', 'bar', 'hist', 'figure', 'subplot', 'show', 'savefig'],
  'torch': ['tensor', 'cuda', 'nn', 'optim', 'autograd', 'utils', 'save', 'load'],
  'tensorflow': ['keras', 'constant', 'Variable', 'GradientTape', 'function', 'config'],
};

// GPU-specific completions
const GPU_COMPLETIONS = [
  { label: 'torch.cuda.is_available()', type: 'function', detail: 'Check if CUDA is available' },
  { label: 'torch.cuda.device_count()', type: 'function', detail: 'Get number of GPUs' },
  { label: 'torch.cuda.get_device_name()', type: 'function', detail: 'Get GPU device name' },
  { label: 'torch.cuda.memory_allocated()', type: 'function', detail: 'Get allocated GPU memory' },
  { label: 'torch.cuda.memory_reserved()', type: 'function', detail: 'Get reserved GPU memory' },
  { label: 'torch.cuda.empty_cache()', type: 'function', detail: 'Clear GPU cache' },
  { label: '.to("cuda")', type: 'method', detail: 'Move tensor to GPU' },
  { label: '.cuda()', type: 'method', detail: 'Move tensor to GPU' },
  { label: '.cpu()', type: 'method', detail: 'Move tensor to CPU' },
  { label: 'with torch.cuda.device(0):', type: 'snippet', detail: 'GPU device context' },
];

// Python built-ins and keywords
const PYTHON_BUILTINS = [
  // Built-in functions
  'abs', 'all', 'any', 'ascii', 'bin', 'bool', 'bytes', 'callable', 'chr', 'classmethod',
  'compile', 'complex', 'delattr', 'dict', 'dir', 'divmod', 'enumerate', 'eval', 'exec',
  'filter', 'float', 'format', 'frozenset', 'getattr', 'globals', 'hasattr', 'hash', 'help',
  'hex', 'id', 'input', 'int', 'isinstance', 'issubclass', 'iter', 'len', 'list', 'locals',
  'map', 'max', 'memoryview', 'min', 'next', 'object', 'oct', 'open', 'ord', 'pow', 'print',
  'property', 'range', 'repr', 'reversed', 'round', 'set', 'setattr', 'slice', 'sorted',
  'staticmethod', 'str', 'sum', 'super', 'tuple', 'type', 'vars', 'zip',
  // Keywords
  'False', 'None', 'True', 'and', 'as', 'assert', 'async', 'await', 'break', 'class',
  'continue', 'def', 'del', 'elif', 'else', 'except', 'finally', 'for', 'from', 'global',
  'if', 'import', 'in', 'is', 'lambda', 'nonlocal', 'not', 'or', 'pass', 'raise', 'return',
  'try', 'while', 'with', 'yield',
];

// Magic commands for Jupyter
const MAGIC_COMMANDS = [
  { label: '%matplotlib inline', type: 'magic', detail: 'Enable inline plotting' },
  { label: '%time', type: 'magic', detail: 'Time execution of a Python statement' },
  { label: '%timeit', type: 'magic', detail: 'Time execution of a Python statement multiple times' },
  { label: '%%time', type: 'magic', detail: 'Time execution of entire cell' },
  { label: '%%timeit', type: 'magic', detail: 'Time execution of entire cell multiple times' },
  { label: '%who', type: 'magic', detail: 'List variables' },
  { label: '%whos', type: 'magic', detail: 'List variables with details' },
  { label: '%reset', type: 'magic', detail: 'Reset namespace' },
  { label: '%run', type: 'magic', detail: 'Run a Python script' },
  { label: '%load', type: 'magic', detail: 'Load code from a file' },
  { label: '%store', type: 'magic', detail: 'Store variables for later use' },
  { label: '%env', type: 'magic', detail: 'Get/set environment variables' },
  { label: '!nvidia-smi', type: 'shell', detail: 'Show GPU status' },
];

export function pythonCompletions(context: CompletionContext): CompletionResult | null {
  const word = context.matchBefore(/[\w%!]*/);
  if (!word || (word.from === word.to && !context.explicit)) {
    return null;
  }

  const options = [];
  const wordText = word.text.toLowerCase();

  // Magic commands
  if (wordText.startsWith('%') || wordText.startsWith('!')) {
    options.push(...MAGIC_COMMANDS.filter(cmd => 
      cmd.label.toLowerCase().startsWith(wordText)
    ));
  }

  // GPU-specific completions
  if (wordText.includes('cuda') || wordText.includes('gpu')) {
    options.push(...GPU_COMPLETIONS);
  }

  // Check for import statements
  const line = context.state.doc.lineAt(context.pos).text;
  if (line.includes('import') || line.includes('from')) {
    // Add module suggestions
    Object.keys(COMMON_IMPORTS).forEach(module => {
      if (module.toLowerCase().includes(wordText)) {
        options.push({
          label: module,
          type: 'module',
          detail: `Import ${module}`,
        });
      }
    });

    // If importing from a module, suggest its members
    Object.entries(COMMON_IMPORTS).forEach(([module, members]) => {
      if (line.includes(`from ${module}`)) {
        members.forEach(member => {
          if (member.toLowerCase().includes(wordText)) {
            options.push({
              label: member,
              type: 'function',
              detail: `From ${module}`,
            });
          }
        });
      }
    });
  }

  // Python built-ins
  PYTHON_BUILTINS.forEach(builtin => {
    if (builtin.toLowerCase().startsWith(wordText)) {
      options.push({
        label: builtin,
        type: builtin === builtin.toUpperCase() ? 'constant' : 'function',
      });
    }
  });

  // Common patterns
  if (wordText === '' && context.explicit) {
    options.push(
      { label: 'import numpy as np', type: 'snippet', detail: 'Import NumPy' },
      { label: 'import pandas as pd', type: 'snippet', detail: 'Import Pandas' },
      { label: 'import matplotlib.pyplot as plt', type: 'snippet', detail: 'Import Matplotlib' },
      { label: 'import torch', type: 'snippet', detail: 'Import PyTorch' },
      { label: 'import tensorflow as tf', type: 'snippet', detail: 'Import TensorFlow' },
    );
  }

  if (options.length === 0) {
    return null;
  }

  return {
    from: word.from,
    options,
  };
}

// Variable tracking for smart completions
export class VariableTracker {
  private variables: Map<string, { type: string; value?: any }> = new Map();

  addVariable(name: string, type: string, value?: any) {
    this.variables.set(name, { type, value });
  }

  getVariables(): Array<{ label: string; type: string; detail?: string }> {
    return Array.from(this.variables.entries()).map(([name, info]) => ({
      label: name,
      type: 'variable',
      detail: info.type,
    }));
  }

  clear() {
    this.variables.clear();
  }
}